import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import branding from '../branding.json';
import ReactMarkdown from 'react-markdown';
import { FiDownload } from 'react-icons/fi';

const LEGAL_DOCS: Record<string, { title: string; file: string }> = {
  terms: { title: 'Terms of Service', file: 'terms-v1' },
  privacy: { title: 'Privacy Policy', file: 'privacy-v1' },
};

export const Legal: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { darkMode } = useTheme();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const doc = searchParams.get('doc') || 'terms';
  const docInfo = LEGAL_DOCS[doc];

  useEffect(() => {
    const loadDoc = async () => {
      setLoading(true);
      try {
        const module = await import(`../legal/${docInfo?.file}.md?raw`);
        // Strip HTML comments (e.g. the pre-launch checklist header)
        const stripped = module.default.replace(/<!--[\s\S]*?-->/g, '').trimStart();
        setContent(stripped);
      } catch {
        setContent(
          '# Document Not Found\n\nThe requested legal document could not be loaded.'
        );
      }
      setLoading(false);
    };
    loadDoc();
  }, [doc, docInfo?.file]);

  useEffect(() => {
    document.title = `${docInfo?.title || 'Legal'} - ${branding.appName}`;
  }, [docInfo?.title]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  const legalBranding = (branding as any).legal;

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${docInfo?.file}.md`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Navigation between legal docs + download */}
      <div
        className={`flex items-center space-x-4 mb-6 text-sm ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}
      >
        {Object.entries(LEGAL_DOCS).map(([key, info]) => (
          <Link
            key={key}
            to={`/legal?doc=${key}`}
            className={`hover:underline ${
              doc === key
                ? darkMode
                  ? 'text-white font-semibold'
                  : 'text-gray-900 font-semibold'
                : ''
            }`}
          >
            {info.title}
          </Link>
        ))}
        <button
          onClick={handleDownload}
          disabled={loading}
          className={`ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors
            ${darkMode
              ? 'border-gray-600 text-gray-400 hover:text-white hover:border-gray-400'
              : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-500'
            }`}
        >
          <FiDownload size={13} />
          Download .md
        </button>
      </div>

      {/* Document content */}
      <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>

      {/* Footer */}
      <div
        className={`mt-12 pt-6 border-t text-sm ${
          darkMode
            ? 'border-gray-700 text-gray-400'
            : 'border-gray-200 text-gray-500'
        }`}
      >
        <p>
          Questions? Contact us at{' '}
          <a
            href={`mailto:${
              legalBranding?.complianceContact || branding.companyInfo.email
            }`}
            className="underline"
          >
            {legalBranding?.complianceContact || branding.companyInfo.email}
          </a>
        </p>
      </div>
    </div>
  );
};
